import * as _ from 'lodash'
import {
    ICell,
    ICellMetric,
    ICellMetricAggregator,
    ICellMetricAggregatorCalcLevel,
    ICellMetricPushDownMethod, ICellMetricUOM,
    ICubeSource
} from "./PA.HyperRectangle.IF";
import {BehaviorSubject} from "rxjs/Rx";
import {CellMetricUtils, ICellMetricCalcPass} from "./CellMetricUtils";
import {isPromise} from "../PA.Core/Basic/ASync";
import {Perspective} from "./Perspective";


export class PerspectiveLocalCell
    implements ICell
{

    private _cubeSource: ICubeSource
    private _perspective: Perspective
    private _cellId: string
    private _cells: ICell[]
    private _cellEnabled: boolean
    private _OnCellChange: BehaviorSubject<{ Cell: ICell; MetricIds?: string[] }>;
    private _bMetricValuesLoaded: boolean
    private _cellDimensionKeys: {[dimId:string]: string}

    private _cellMetric:          {[metricId:string]: ICellMetric}
    private _cellMetricValue:     {[metricId:string]: number | string}
    private _refreshMetricValuesPromise: Promise<void>

    constructor(cubeSource:           ICubeSource,
                perspective:          Perspective,
                cellId:               string,
                cellDimKeys:          {[dimTypeId:string]: string},
                cellMetricIds:        string[],
                arrLeafCells:         ICell[],
                bInitializeMetrics?:  boolean,
                bEnabled?: boolean)
    {

        this._cubeSource = cubeSource
        this._perspective = perspective
        this._cellId = cellId
        this._bMetricValuesLoaded = false
        this._cells = arrLeafCells
        this._cellMetric = {}
        this._refreshMetricValuesPromise = undefined
        this._cellDimensionKeys = _.clone(cellDimKeys)
        this._cellMetricValue = {}
        this._cellEnabled = true
        if(_.isBoolean(bEnabled))
            this._cellEnabled = bEnabled
        let dctMetrics =  _.keyBy(this._cubeSource.getMetrics(), (a) => a.MetricId)

        _.forEach(cellMetricIds, (aMetricId) => {
            let aMetric = dctMetrics[aMetricId]
            if(_.isEmpty(aMetric)){
                console.log("Unknown Perspective Metric Id: ", aMetricId)
                return
            }

            this._cellMetric[aMetricId] = aMetric
        })

        this._OnCellChange = new BehaviorSubject<{Cell: ICell; MetricId?: string}>(undefined)

        if(bInitializeMetrics)
            this.refreshMetricValues()
    }

    get CellId(): string {
        return this._cellId
    }

    get bCellEnabled(): boolean { return this._cellEnabled };


    get OnCellChange(): BehaviorSubject<{ Cell: ICell; MetricIds?: string[] }> {
        return this._OnCellChange
    }

    get bMetricValuesLoaded(): boolean {
        return this._bMetricValuesLoaded
    }

    getCellMetricValue(metricId: string): string | number {
        return this._cellMetricValue[metricId];
    }

    getCellMetricUOM(metricId: string): ICellMetricUOM {
        return this._perspective.getCellMetricUOM(metricId);
    }


    getKey(dimensionTypeId: string): string {
        return this._cellDimensionKeys[dimensionTypeId];
    }

    getKeys(): { [dimTypeId: string]: string } {
        return _.clone(this._cellDimensionKeys);
    }

    async refreshMetricValues(metricIds?: string[], bSuppressNotification?: boolean): Promise<void> {
        const self = this

        try {

            if (_.isEmpty(this._cells)) {
                // Reset to zero
                return
            }

            // First Pass metrics are the additive metrics, order them
            let calcPasses: ICellMetricCalcPass[]
            let arrMetricIds: string[]

            if (_.isEmpty(metricIds)) {
                arrMetricIds = _.keys(this._cellMetric)
                calcPasses = self._perspective.getMetricCalcPasses()
            } else {
                arrMetricIds = metricIds
                calcPasses = CellMetricUtils.getCalcPasses(this._cubeSource, arrMetricIds)
            }

            // Determine UOM/ Converters for all Metrics

            for (let aCalcPass of calcPasses) {

                let dctAggregators: { [metricId: string]: ICellMetricAggregator } = {}

                //-- Create Initial Aggregators
                for (let aMetricId of aCalcPass.Metrics) {
                    let metric: ICellMetric = undefined

                    if (aMetricId in this._cellMetric)
                        metric = this._cellMetric[aMetricId]
                    else {
                        metric = this._cubeSource.getMetric(aMetricId)
                        if (metric)
                            this._cellMetric[aMetricId] = metric
                    }

                    if (metric) {
                        let aggregator = metric.Aggregator()
                        if (aggregator)
                            dctAggregators[metric.MetricId] = aggregator
                    }
                }

                switch (aCalcPass.Level) {

                    case ICellMetricAggregatorCalcLevel.Leaf:
                        for (let metricId in dctAggregators) {
                            let aggr = dctAggregators[metricId]
                            let metric: ICellMetric = undefined

                            if (metricId in this._cellMetric)
                                metric = this._cellMetric[metricId]
                            else
                                metric = this._cubeSource.getMetric(metricId)

                            //-- Check for Metric Conversion
                            let conversions = self._perspective.getCellMetricConversions(metricId)

                            for (let aCell of this._cells) {
                                if (_.isEmpty(conversions)) {
                                    aggr.add(aCell)
                                } else {
                                    let convertOptions = aCell.getUOMConvertOptions(metricId)

                                    // Avoid async function for performance
                                    // -- therefore, dup numerator and denominator code
                                    // -- condition await

                                    let aConversionFactor_N = 1

                                    for (let aConv of conversions.NumeratorConversions) {
                                        let convRate = aConv.converter.conversionRate(aConv.fromUOM, aConv.toUOM, convertOptions)
                                        if (isPromise(convRate))
                                            convRate = await convRate

                                        if (_.isUndefined(convRate) || _.isNaN(convRate)) {
                                            // Error determining Conversion rate
                                            aConv.errorCount |= 0
                                            aConv.errorCount++

                                            let errMsg = `Unable to convert ${aConv?.convertedId}`
                                            //TODO Index, Increment Message Provider Error Count
                                            aConversionFactor_N = undefined
                                            break
                                        }

                                        aConversionFactor_N *= convRate
                                    }

                                    let aConversionFactor_D = 1
                                    for (let aConv of conversions.DenominatorConversions) {
                                        let convRate = aConv.converter.conversionRate(aConv.fromUOM, aConv.toUOM, convertOptions)
                                        if (isPromise(convRate))
                                            convRate = await convRate

                                        if (_.isUndefined(convRate) || _.isNaN(convRate)) {
                                            // Error determining Conversion rate
                                            aConv.errorCount |= 0
                                            aConv.errorCount++

                                            let errMsg = `Unable to convert ${aConv?.convertedId}`
                                            //TODO Index, Increment Message Provider Error Count
                                            aConversionFactor_D = undefined
                                            break
                                        }

                                        aConversionFactor_D *= convRate
                                    }


                                    // Determine combined conversion factor
                                    if (_.isNumber(aConversionFactor_N) && _.isNumber(aConversionFactor_D) && (aConversionFactor_N != 0)) {
                                        let aFactor = aConversionFactor_N / aConversionFactor_D
                                        aggr.add(aCell, aFactor)
                                    } else {
                                        //TODO Index, Increment Message Provider Error Count
                                        let errMsg = `Unable to convert, Conversion Rate NAN}`
                                    }
                                }
                            }
                        } //-- Next Metric
                        break;

                    case ICellMetricAggregatorCalcLevel.Perspective:
                        for (let metricId in dctAggregators) {
                            let aggr = dctAggregators[metricId]
                            let metric: ICellMetric = undefined

                            if (metricId in this._cellMetric)
                                metric = this._cellMetric[metricId]
                            else
                                metric = this._cubeSource.getMetric(metricId)

                            aggr.add(this)

                            // TODO:  No converters for Perspective Level Calc?
                        }

                        break;
                }

                // Write Aggregations to perspective cell
                _.forEach(dctAggregators, (aggr, aggMetricId) => {
                    this._cellMetricValue[aggMetricId] = aggr.getResults()
                })
            }

            this._bMetricValuesLoaded = true
            this._OnCellChange.next({Cell: this, MetricIds: arrMetricIds})

            if (!bSuppressNotification)
                this._perspective._notifyCellUpdate(this, arrMetricIds)

        } catch (err) {
            console.log(err)
        }
    }

    setCellMetricValue(MetricId: string, pValue: number | string): void {
        const self = this

        let newValue = _.toNumber(pValue)
        // This pushes down to Leaf Nodes
        let cellMetric = self._cellMetric[MetricId]

        if(_.isEmpty(cellMetric))
            return

        if(cellMetric.bIsReadOnly)
            return

        // Do this async
        (async () => {
            switch (cellMetric.PushDownMethod) {
                case ICellMetricPushDownMethod.None:
                    self._cellMetricValue[MetricId] = newValue
                    return

                case ICellMetricPushDownMethod.CopyDown:
                    await CellMetricUtils.setValueCopyDown(self._perspective, self._cells, cellMetric.MetricId, newValue)
                    break

                case ICellMetricPushDownMethod.ScaleBy:
                    let existingValue = _.toNumber(self._cellMetricValue[MetricId])
                    await CellMetricUtils.setValueScaleBy(self._perspective, self._cells, MetricId, existingValue, newValue)
                    break

                case ICellMetricPushDownMethod.Reallocate:
                    await CellMetricUtils.setValueReallocate(self._perspective, self._cells, MetricId, newValue)
                    break;

                case ICellMetricPushDownMethod.AllocateBy:
                    let allocByMetricId = cellMetric.PushDownOptions?.AllocateByMetricId || MetricId
                    await CellMetricUtils.setValueAllocateBy(self._perspective, self._cells, MetricId, allocByMetricId, newValue)
                    break
            }

            // Refresh Aggregate
            let relatedMetrics = self._cubeSource.getRefreshMetricIds(MetricId)
            self.refreshMetricValues(relatedMetrics)

        })();

    }




}